import { vec2 } from "@tlaukkan/tsm";
import { Entity } from 'ecsy';
import * as PIXI from 'pixi.js';
import { Renderable, Transform, Virus, WaterTrail } from '../../../shared/components';
import { Background, Camera, Resolution } from "../../../shared/constants";
import { BackgroundLayer } from './backgroundLayer';
import { RenderEngine } from './renderEngine';
import backgroundNoise from "./shaders/fragment/backgroundNoise";
import trailsFrag from "./shaders/fragment/trails";
import defaultVert from "./shaders/vertex/default";
import trailsVert from "./shaders/vertex/trails";
import { createQuadMesh } from './spriteFactory';
import { angleFromUp, metersToPixels } from "../../../shared/utils";
import { Colors } from "../palettes/colorPalette";
import { cellLocations } from "../../../shared/generated/assetLocations";

/**Converts a world position (meters) into the trails texture's space, before scaling by TrailsTextureScaleFactor */
export function worldToTrailSpace(worldX: number, worldY: number, dest = new vec2()) {
	const cameraSpaceX = metersToPixels(worldX) + Resolution.Width / 2;
	const cameraSpaceY = metersToPixels(-worldY) + (Resolution.Height / 2);
	dest.xy = [
		cameraSpaceX + Resolution.Width * Background.TrailsTexturePadding,
		cameraSpaceY + Resolution.Height * Background.TrailsTexturePadding
	];
	return dest;
}



export class BackgroundFluid implements BackgroundLayer {
	mesh: PIXI.Mesh;
	private background = new PIXI.Program(defaultVert, backgroundNoise);

	imprintContainer = new PIXI.Container();

	private trailMesh: PIXI.Mesh;
	private trailsContainer: PIXI.Container;
	private backgroundTrails = new PIXI.Program(trailsVert, trailsFrag);

	private imprintTexture: PIXI.RenderTexture

	private activeTrailTexture: 0 | 1 = 0;
	private trailTextures = new Array<PIXI.RenderTexture>();

	private trailEntities = new Array<Entity>();

	constructor(private engine: RenderEngine, private sceneRoot: PIXI.Container, private renderer: PIXI.Renderer) {
		this.imprintTexture = PIXI.RenderTexture.create({ width: Resolution.Width, height: Resolution.Height });

		for (let i = 0; i < 2; i++) {
			this.trailTextures.push(PIXI.RenderTexture.create({ width: Resolution.Width, height: Resolution.Height }));
		}

		this.trailsContainer = new PIXI.Container();
		this.trailMesh = createQuadMesh(Resolution.Width, Resolution.Height, this.backgroundTrails, {
			imprint: this.imprintTexture,
			previousTrails: this.trailTextures[this.getPreviousTrailIndex()],
			positionOffset: [0, 0],
			scaleOffset: [0, 0],
			time: 0,
			noise: [],
		})
		this.trailMesh.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
		this.trailsContainer.addChild(this.trailMesh);

		//Create the background
		this.mesh = createQuadMesh(Resolution.Width, Resolution.Height, this.background, {
			time: 0,
			color: PIXI.utils.hex2rgb(Colors.Background),
			darkColor: PIXI.utils.hex2rgb(Colors.BackgroundRaised),
			trails: this.trailTextures[this.activeTrailTexture],
			imprint: this.imprintTexture,
			texelSize: [1 / Resolution.Width, 1 / Resolution.Height],
			cameraPosition: [0, 0],
			cameraScale: 1,
			trailUvScaler: Background.TrailsTextureScaleFactor,
			trailUvOffset: [0, 0],
			lightPos: this.engine.lightSource.xy,
			noise: [], //hack because its not loaded yet probably
		});
		this.mesh.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
	}

	getTrailsTexture() {
		return this.trailTextures[this.activeTrailTexture];
	}

	getPreviousTrailIndex() {
		return (this.activeTrailTexture === 0 ? 1 : 0);
	}

	trailsFaster = false;
	accelerateTrails(duration = 0.5) {
		this.trailsFaster = true;
		setTimeout(() => {
			this.trailsFaster = false;
		}, duration * 1000);
	}

	private trailsSpaceScratch = new vec2();
	private lastFrameScenePos: vec2 | null = null;
	private noiseTextureIsSet = false;
	private noiseTime = 0;

	update(delta: number, time: number, cameraPos: vec2, cameraScale: number) {
		//Match size of the parent container
		this.mesh.position.set(-this.sceneRoot.x * (1 / this.sceneRoot.scale.x), -this.sceneRoot.y * (1 / this.sceneRoot.scale.y));
		this.mesh.scale.set(1 / this.sceneRoot.scale.x, 1 / this.sceneRoot.scale.y);

		for (const entity of this.trailEntities) {
			const renderable = entity.getComponent(Renderable);
			const waterTrail = entity.getMutableComponent(WaterTrail);
			const transform = entity.getComponent(Transform);

			const sprite = waterTrail.trailSprite;
			let thisFrame = renderable.positionRadiusHistory[0];
			let rotation = thisFrame.rotation;
			let worldPositionX = transform.position.x;
			let worldPositionY = transform.position.y;

			//Stretch sprites so they extend to the previous position. TODO: Does NOT work for rectanglular sprites!
			let stetchScaleY = 1;
			if (transform.size.x === transform.size.y && renderable.positionRadiusHistory.length > 1) {
				const prevFrame = renderable.positionRadiusHistory[1];
				const direction = vec2.difference(prevFrame.position, thisFrame.position);
				const distance = direction.length();
				if (distance > 0) {
					worldPositionX = (prevFrame.position.x + worldPositionX) / 2;
					worldPositionY = (prevFrame.position.y + worldPositionY) / 2;
					rotation = -angleFromUp(direction.normalize());
					stetchScaleY = 1 + (distance / (transform.size.y / 2));
				}
			}

			const trailsSpace = worldToTrailSpace(worldPositionX, worldPositionY, this.trailsSpaceScratch);
			sprite.position.set(trailsSpace.x * Background.TrailsTextureScaleFactor, trailsSpace.y * Background.TrailsTextureScaleFactor);

			let pad = entity.hasComponent(Virus) ? cellLocations.virus.width : cellLocations.bloodcell.width
			pad *= Background.WaterImprintAddedScale

			sprite.rotation = rotation;
			const scalex = (sprite.width + pad) / sprite.width;
			const scaley = (sprite.height + pad) / sprite.height;
			sprite.width = metersToPixels(transform.size.x * Background.TrailsTextureScaleFactor * scalex);
			sprite.height = metersToPixels((transform.size.y) * Background.TrailsTextureScaleFactor * scaley);
			sprite.scale.set(sprite.scale.x, sprite.scale.y * stetchScaleY);
		}

		this.mesh.shader.uniforms.time += 0.0001;
		this.mesh.shader.uniforms.cameraPosition = [
			this.sceneRoot.x / (Resolution.Width * this.sceneRoot.scale.x),
			this.sceneRoot.y / (Resolution.Height * this.sceneRoot.scale.y)
		];
		this.mesh.shader.uniforms.cameraScale = cameraScale;
		this.mesh.shader.uniforms.lightPos = this.engine.lightSource.xy;

		this.mesh.shader.uniforms.trailUvOffset = [
			(cameraScale * Background.TrailsTexturePadding) - (cameraPos.x / Resolution.Width),
			(cameraScale * Background.TrailsTexturePadding) - (cameraPos.y / Resolution.Height),
		];
		this.mesh.shader.uniforms.trailUvScaler = Background.TrailsTextureScaleFactor / cameraScale;

		this.renderer.render(this.imprintContainer, this.imprintTexture);

		if (this.lastFrameScenePos === null) {
			this.lastFrameScenePos = new vec2();
		}

		//Trail mesh
		const deltaMS = delta * 0.001;
		const noiseScrollSpeed = 0.004;
		this.trailMesh.shader.uniforms.time += (deltaMS * noiseScrollSpeed);
		if (this.trailMesh.shader.uniforms.time > 1.0) {
			this.trailMesh.shader.uniforms.time -= 1.0;
		}

		this.trailMesh.shader.uniforms.additionTrailFade = this.trailsFaster ? 0.15 : 0.0;

		this.trailMesh.shader.uniforms.cameraPos = [
			(this.lastFrameScenePos.x - cameraPos.x) / (Resolution.Width * this.sceneRoot.scale.x), //WTF?? shouldn't this be normalized?? but somehow this fixes scale issues or seems to?
			(this.lastFrameScenePos.y - cameraPos.y) / (Resolution.Height * this.sceneRoot.scale.y)
		];

		this.lastFrameScenePos.xy = [cameraPos.x, cameraPos.y];

		this.renderer.render(this.trailsContainer, this.trailTextures[this.activeTrailTexture]);
		this.trailMesh.shader.uniforms.previousTrails = this.trailTextures[this.activeTrailTexture];
		if (!this.noiseTextureIsSet) {
			const texture = this.engine.spriteFactory.getTexture("displacementMap");
			texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
			this.trailMesh.shader.uniforms.noise = texture;

			this.noiseTextureIsSet = true;
		}

		this.activeTrailTexture = this.getPreviousTrailIndex(); // Swap active trail texture so we can use this one as input for the next one
	}

	addSprite(sprite: PIXI.Sprite) {
		this.imprintContainer.addChild(sprite);
	}

	updateTrailEntities(entities: Array<Entity>) {
		this.trailEntities = entities;
	}
}